// Production error capture for a no-backend, single-user app. A server-less app
// has no server logs, so an uncaught error or unhandled promise rejection would
// otherwise vanish silently, the way the MusicButler feed failure did until it
// was noticed by hand. Every failure goes two places:
//
//   1. the in-memory sync log that Settings already surfaces (kind 'error'), which
//      answers "did anything go wrong THIS session", and
//   2. a localStorage buffer that is flushed to a per-device markdown file in the
//      app's own OneDrive folder on the next successful sync (see errorFlush.ts),
//      which is what lets a weekly routine find bugs that would otherwise go
//      unreported. See RESUME-ERROR-SYNC.md in the claude-skills-and-routines
//      repo for the full design.
//
// CANONICAL MODULE — app-platform/shared/errorLog.ts. This file (and errorFlush.ts /
// expectations.ts) is copied byte-for-byte into all five apps. It imports nothing from the
// app. App-specific facts (app name, build hash, the OneDrive read/write helpers,
// the device id, the session log to mirror into) come in through
// configureErrorSync(), never by editing the module body; the one exception is
// the SYNC_PATTERNS table, which each app grows with its own sync noise.
//
// Fix bugs in the HUB copy, then run `node scripts/sync-shared.mjs` in each app.
// (Until 2026-09-18 this said "fix Contently's copy first, then copy" — that was the
// convention before app-platform/shared existed. Contently's copy is now a copy like
// any other, and each app's drift test fails if one is edited in place.)
//
// Rules this module keeps, from the design:
//   - Classify at capture time. Failures that are OneDrive being OneDrive (412,
//     409, 429, 423, network offline, token expiry) are tagged `sync`, everything
//     else `error`, so the routine can look at volume rather than guess. An Error
//     is classified on its name AND its message (W10), since the typed transport
//     errors carry the signal in the name.
//   - Never write the log through the thing that is failing: capture only
//     buffers; the flush happens after a sync that succeeded.
//   - The reporter itself can never throw.

export type ErrorSeverity = 'sync' | 'error' | 'degraded'

export interface ErrorEntry {
  /** ISO-8601 UTC, second precision. */
  at: string
  severity: ErrorSeverity
  /** Route the app was on when it happened (pathname). */
  route: string
  /** Short build hash the entry was captured under. Vite renames chunks every
   *  build, so a file:line without it is useless a week later. */
  build: string
  /** One line: the message, then the top stack frame as `basename:line` when known. */
  message: string
}

export interface ErrorTransport {
  /** Read a text file from the app's OneDrive folder; null when it does not exist.
   *  `token` is whatever the app passed to flushErrorLog (an access token, or ''
   *  for apps whose Graph helper acquires its own). */
  read(token: string, name: string): Promise<string | null>
  /** Write (create or replace) a text file in the app's OneDrive folder. */
  write(token: string, name: string, text: string): Promise<void>
}

export interface ErrorSyncConfig {
  /** Lower-case app id used in entries, storage keys and the file header. */
  app: string
  /** Short build hash (or 'dev'). */
  build: string
  /** OneDrive read/write; without it the buffer is kept but never flushed. */
  transport?: ErrorTransport
  /** Stable per-device id; defaults to a 6-char id stored in localStorage. */
  deviceId?: () => string
  /** Human description of this device for the file header (name, user agent). */
  describeDevice?: () => string
  /** Current route; defaults to location.pathname. */
  route?: () => string
  /** Clock; injectable for tests. */
  now?: () => number
  /** Mirror each captured entry into the app's own in-memory session log (the
   *  view Settings shows), if it has one. Errors thrown here are swallowed. */
  onRecord?: (entry: ErrorEntry) => void
}

/**
 * Buffered entries kept in localStorage between flushes (oldest dropped first).
 * Exported for tests only (app-platform 8.3): no app code calls it.
 */
export const BUFFER_CAP = 200
/** An identical message seen again within this window is skipped: a tight render
 *  loop or a retry storm can throw the same error many times a second. */
const COALESCE_MS = 4000
const MAX_MESSAGE = 500

let cfg: ErrorSyncConfig = { app: 'app', build: 'dev' }
let lastSig = ''
let lastAt = 0
let installed = false

export function configureErrorSync(next: ErrorSyncConfig): void {
  cfg = next
}

export function errorSyncConfig(): ErrorSyncConfig {
  return cfg
}

// ---------------------------------------------------------------- classifier
// Message shapes that are OneDrive / network / auth behaviour rather than a bug
// in the app. A match is tagged `sync`. The weekly triage routine appends to this
// table when it decides an `error` entry was really sync noise, so the residual
// it has to guess about shrinks each week. Keep each pattern narrow enough that a
// genuine bug cannot hide behind it (a bare /conflict/ would swallow too much).
const SYNC_PATTERNS: ReadonlyArray<{ re: RegExp; why: string }> = [
  { re: /\b412\b|precondition\s*failed|DocConflictError|write conflict|another device wrote first/i, why: 'eTag precondition (412): another device wrote first' },
  { re: /\b409\b|nameAlreadyExists|resource already exists/i, why: 'Graph conflict (409): item already exists' },
  { re: /\b429\b|throttl|too many requests|activityLimitReached/i, why: 'Graph throttling (429)' },
  { re: /\b423\b|resource is locked|notAllowed.*locked/i, why: 'Graph locked (423)' },
  { re: /\b50[234]\b|service\s*unavailable|serviceNotAvailable|bad gateway|gateway time-?out/i, why: 'Graph or Cloudflare transient (502/503/504)' },
  { re: /Graph (write|cover upload|create[- ]folder|upload|delete)[^\n]*\b404\b/i, why: '404 on a path the app just created (folder not yet visible)' },
  { re: /Failed to fetch|NetworkError|network request failed|ERR_INTERNET_DISCONNECTED|ERR_NETWORK|Load failed|\boffline\b|net::ERR_/i, why: 'network offline or interrupted' },
  { re: /\b401\b|InteractionRequired|interaction_required|login_required|consent_required|AADSTS\d+|token (has )?expired|invalid_grant|monitor_window_timeout|BrowserAuthError|acquireToken/i, why: 'MSAL token expiry or interaction required (401)' },
  { re: /Sign-in needs attention|redirect didn.t complete/i, why: 'MSAL redirect did not complete (auth, not app)' },
  // W10's typed transport errors (app-platform shared/graphErrors.ts, shared/authToken.ts). Their
  // messages are sentences for a person, so these match on the error NAME — see classifyError.
  { re: /\b(InteractiveSignInRequiredError|GraphNetworkError|GraphThrottledError)\b/, why: 'typed sync transport error (sign-in, network, throttle)' },
  // W11: conflictPolicy.decideConflict's `done` log for an If-Match write Graph answered by
  // recreating a file another device had deleted. Expected sync behaviour, not a defect (2026-10-08).
  { re: /had been deleted on another device and was recreated/i, why: 'W11 recreated write (deleted elsewhere, this device\'s copy kept)' },
]

/**
 * `sync` when the message matches a known OneDrive/network/auth shape, else `error`.
 * Exported for tests only (app-platform 8.3): no app code calls it.
 */
export function classifySeverity(message: string): Exclude<ErrorSeverity, 'degraded'> {
  return SYNC_PATTERNS.some((p) => p.re.test(message)) ? 'sync' : 'error'
}

/** Classify a captured reason on `${name}: ${text}`, not the text alone (W10, design §6.1): a
 *  typed error such as `DocConflictError` or `InteractiveSignInRequiredError` carries its sync
 *  signal in its NAME, and its message is a friendly sentence that matches no pattern. Until
 *  2026-10-07 the classifier saw only `message || name`, so those were filed as real errors. The
 *  name is used to classify only; the recorded entry text is unchanged. */
function classifyError(reason: unknown, text: string): Exclude<ErrorSeverity, 'degraded'> {
  const name = reason instanceof Error && reason.name ? `${reason.name}: ` : ''
  return classifySeverity(`${name}${text}`)
}

// ---------------------------------------------------------------- helpers
function stringifyReason(reason: unknown): string {
  if (reason instanceof Error) return reason.message || reason.name || 'Error'
  if (typeof reason === 'string') return reason
  try {
    return JSON.stringify(reason) ?? String(reason)
  } catch {
    return String(reason)
  }
}

function basename(url: string): string {
  const clean = url.split(/[?#]/)[0] ?? url
  return clean.split('/').pop() || clean
}

/** The first stack frame that names a file, as `basename:line`, or undefined.
 *  Handles Chrome (`at fn (https://x/a.js:12:3)`) and Firefox/Safari
 *  (`fn@https://x/a.js:12:3`) shapes; frames inside this module are skipped so a
 *  reported error points at its caller, not at the reporter.
 * Exported for tests only (app-platform 8.3): no app code calls it.
 */
export function topFrame(err: unknown): string | undefined {
  const stack = err instanceof Error ? err.stack : undefined
  if (!stack) return undefined
  for (const raw of stack.split('\n')) {
    const m = /([^\s()@]+?):(\d+)(?::\d+)?\)?\s*$/.exec(raw.trim())
    if (!m) continue
    const file = m[1] ?? ''
    if (!/[./]/.test(file)) continue // "Error: message" lines never name a file
    const base = basename(file)
    if (/^errorLog\b|^errorFlush\b|^expectations\b/.test(base)) continue
    return `${base}:${m[2]}`
  }
  return undefined
}

/** ISO-8601 UTC at second precision, e.g. 2026-09-14T08:52:11Z. */
export function isoSeconds(ms: number): string {
  return new Date(ms).toISOString().replace(/\.\d{3}Z$/, 'Z')
}

function nowMs(): number {
  try {
    return cfg.now ? cfg.now() : Date.now()
  } catch {
    return Date.now()
  }
}

function currentRoute(): string {
  try {
    if (cfg.route) return cfg.route()
    return typeof location !== 'undefined' ? location.pathname : ''
  } catch {
    return ''
  }
}

// ---------------------------------------------------------------- device id
const ID_CHARS = 'abcdefghijklmnopqrstuvwxyz0123456789'

function randomId(len: number): string {
  let out = ''
  try {
    const bytes = crypto.getRandomValues(new Uint8Array(len))
    for (const b of bytes) out += ID_CHARS[b % ID_CHARS.length]
    return out
  } catch {
    for (let i = 0; i < len; i++) out += ID_CHARS[Math.floor(Math.random() * ID_CHARS.length)]
    return out
  }
}

/** This device's id for the errors file name: the configured provider when
 *  there is one, else a 6-char id created once and kept in localStorage. Always
 *  file-name safe. */
export function getErrorDeviceId(): string {
  const clean = (s: string) => s.replace(/[^A-Za-z0-9_-]/g, '').slice(0, 16)
  try {
    if (cfg.deviceId) {
      const id = clean(cfg.deviceId())
      if (id) return id
    }
  } catch {
    /* fall through to the stored id */
  }
  const key = `${cfg.app}.errorSync.deviceId`
  try {
    let id = localStorage.getItem(key)
    if (!id || !clean(id)) {
      id = randomId(6)
      localStorage.setItem(key, id)
    }
    return clean(id) || 'device'
  } catch {
    return 'device'
  }
}

// ---------------------------------------------------------------- buffer
const bufferKey = () => `${cfg.app}.errorSync.buffer`

function isEntry(x: unknown): x is ErrorEntry {
  if (!x || typeof x !== 'object') return false
  const e = x as Record<string, unknown>
  return (
    typeof e.at === 'string' &&
    (e.severity === 'sync' || e.severity === 'error' || e.severity === 'degraded') &&
    typeof e.route === 'string' &&
    typeof e.build === 'string' &&
    typeof e.message === 'string'
  )
}

/** Entries captured on this device and not yet flushed to OneDrive, oldest first. */
export function readErrorBuffer(): ErrorEntry[] {
  try {
    const raw = localStorage.getItem(bufferKey())
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter(isEntry) : []
  } catch {
    return []
  }
}

function writeBuffer(entries: ErrorEntry[]): void {
  try {
    localStorage.setItem(bufferKey(), JSON.stringify(entries))
  } catch {
    /* storage unavailable or full: the session log still has the entry */
  }
}

/** Remove the oldest `count` entries after a successful flush. Entries captured
 *  during the flush sit after them in the buffer and survive. */
export function dropFlushedEntries(count: number): void {
  if (count <= 0) return
  writeBuffer(readErrorBuffer().slice(count))
}

/** @internal Test seam (app-platform 8.3): app and hub tests call it; no app code does. */
export function clearErrorBuffer(): void {
  try {
    localStorage.removeItem(bufferKey())
  } catch {
    /* nothing to clear */
  }
}

// ---------------------------------------------------------------- reporting
function record(severity: ErrorSeverity, text: string): void {
  try {
    const msg = text.slice(0, MAX_MESSAGE)
    const now = nowMs()
    if (msg === lastSig && now - lastAt < COALESCE_MS) return
    lastSig = msg
    lastAt = now
    const entry: ErrorEntry = { at: isoSeconds(now), severity, route: currentRoute(), build: cfg.build, message: msg }
    try {
      cfg.onRecord?.(entry)
    } catch {
      /* the session view is a convenience, never a dependency */
    }
    const buf = readErrorBuffer()
    buf.push(entry)
    writeBuffer(buf.length > BUFFER_CAP ? buf.slice(buf.length - BUFFER_CAP) : buf)
  } catch {
    /* the reporter must never throw */
  }
}

/** Log a caught error (e.g. from the React error boundary) with optional context. */
export function reportError(err: unknown, context?: string): void {
  const msg = stringifyReason(err)
  const frame = topFrame(err)
  const text = `${context ? `${context}: ` : ''}${msg}${frame ? ` (${frame})` : ''}`
  record(classifyError(err, text), text)
}

/** Log an expectation that was not met (nothing threw, but the core path did
 *  nothing when it should have). Always severity `degraded`. */
export function reportDegraded(message: string, context?: string): void {
  record('degraded', context ? `${context}: ${message}` : message)
}

/**
 * Install global handlers for uncaught errors + unhandled promise rejections.
 * Idempotent and safe to call before render. Uses bubble-phase 'error' (which
 * only catches script errors), NOT capture phase, so a broken <img>/cover
 * doesn't spam the log with resource-load noise.
 */
export function installErrorHandlers(): void {
  if (installed || typeof window === 'undefined') return
  installed = true

  window.addEventListener('error', (e: ErrorEvent) => {
    const msg = (e.error instanceof Error ? e.error.message : '') || e.message || 'Unknown error'
    const where = e.filename ? ` (${basename(e.filename)}:${e.lineno})` : ''
    const text = `${msg}${where}`
    record(classifyError(e.error, text), text)
  })

  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    const frame = topFrame(e.reason)
    const text = `unhandled rejection: ${stringifyReason(e.reason)}${frame ? ` (${frame})` : ''}`
    record(classifyError(e.reason, text), text)
  })
}

/** Push buffered entries to this device's errors file in OneDrive. Call after a
 *  sync that SUCCEEDED (so the write path is known-good). Cheap when the buffer
 *  is empty: the flush code is only loaded when there is something to send.
 *  `token` is handed to the transport untouched; apps whose Graph helper acquires
 *  its own token pass nothing. Never rejects. */
export function flushErrorLog(token: string = ''): Promise<boolean> {
  if (!cfg.transport || readErrorBuffer().length === 0) return Promise.resolve(false)
  return import('./errorFlush')
    .then((m) => m.flushErrorBuffer(token))
    .catch(() => false)
}

/**
 * Test hook: forget coalescing state, handlers and config (or install `next`).
 * @internal Test seam (app-platform 8.3): app and hub tests call it; no app code does.
 */
export function _resetErrorSyncForTests(next?: ErrorSyncConfig): void {
  lastSig = ''
  lastAt = 0
  installed = false
  cfg = next ?? { app: 'app', build: 'dev' }
}
